'use client';

import * as React from 'react';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { QueryProvider } from './QueryProvider';
import { ThemeProvider } from './ThemeProvider';
import { Toaster } from '@/shared/ui/sonner';

/**
 * Single composition point for the new (token-based) surfaces: creation
 * widget, public join, post-call. Composition order mirrors the architecture
 * doc — query cache outermost, then theme, then the app.
 *
 * The existing LiveKit meet room renders inside this too but ignores it: it
 * uses its own `data-lk-theme` styling and react-hot-toast, both untouched.
 */
export function AppProviders({ children }: { children: React.ReactNode }) {
  // Toasts arrive top-right on desktop (out of the way of the primary action)
  // and top-center on phones / small screens (thumb-neutral, never covering the
  // bottom control bar). `sm` = 640px, the app's mobile→tablet break.
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const position = isDesktop ? 'top-right' : 'top-center';

  // A crash outside React's render tree (a raw thrown error in an event handler / timer, or a
  // rejected promise nobody caught) never reaches ErrorBoundary at all — before this, it was
  // dropped silently with no trace anywhere, the same "100% silent in production" gap
  // ErrorBoundary itself had. This is the one place mounted for the whole app. Still not a real
  // APM integration (no dashboard/alerting/aggregation) — that needs a real error-tracking
  // account with real credentials this codebase doesn't have configured; this is the honest,
  // credential-free floor: never silent, always at least in the console.
  React.useEffect(() => {
    const onError = (event: ErrorEvent) => {
      console.error('[window.onerror]', event.error ?? event.message);
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      console.error('[unhandledrejection]', event.reason);
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);

  return (
    <QueryProvider>
      <ThemeProvider>
        {children}
        <Toaster position={position} />
      </ThemeProvider>
    </QueryProvider>
  );
}
